import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import "./css/Place.css";

export default function Place({ placeId, onClose }) {

    const [place, setPlace] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Load place by slug
    useEffect(() => {
        if (!placeId) return;

        let mounted = true;

        const loadPlace = async () => {
            try {
                const placeSnap = await getDoc(
                    doc(db, "places", placeId)
                );

                if (placeSnap.exists() && mounted) {
                    setPlace(placeSnap.data());
                }
            } catch (err) {
                console.error("Failed to load place:", err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        loadPlace();

        return () => {
            mounted = false;
        };
    }, [placeId]);


    // Close on ESC
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") onClose?.();
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    // Prevent background scroll while modal is open
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, []);

    if (loading || !place) return null;
    function ImageCarousel({ images }) {
        const [index, setIndex] = useState(0);

        const prev = () => {
            setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
        };

        const next = () => {
            setIndex((i) => (i === images.length - 1 ? 0 : i + 1));
        };

        return (
            <div className="carousel">
                <img
                    src={images[index]}
                    alt={`Slide ${index + 1}`}
                    loading="lazy"
                />

                {images.length > 1 && (
                    <>
                        <button
                            className="carousel-btn prev"
                            onClick={prev}
                            aria-label="Previous image"
                        >
                            ‹
                        </button>
                        <button
                            className="carousel-btn next"
                            onClick={next}
                            aria-label="Next image"
                        >
                            ›
                        </button>

                        <div className="carousel-dots">
                            {images.map((_, i) => (
                                <span
                                    key={i}
                                    className={`dot ${i === index ? "active" : ""}`}
                                    onClick={() => setIndex(i)}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal-box place-modal two-col"
                onClick={(e) => e.stopPropagation()}
            >


                {/* LEFT: Image Carousel */}
                <div className="place-col place-col-image">
                    {Array.isArray(place.images) && place.images.length > 0 && (
                        <ImageCarousel images={place.images} />
                    )}
                </div>


                {/* RIGHT: Content */}
                <div className="place-col place-col-content">
                    <button
                        className="modal-close"
                        aria-label="Close"
                        onClick={onClose}
                    >
                        ✕
                    </button>
                    {place.type && (
                        <span className="place-type">
                            {place.type}
                        </span>
                    )}

                    <h2 className="place-title">{place.name}</h2>

                    {(place.city || place.country) && (
                        <p className="place-location">
                            {place.city}
                            {place.city && place.country ? ", " : ""}
                            {place.country}
                        </p>
                    )}

                    {place.shortDescription && (
                        <p className="place-description">
                            {place.shortDescription}
                        </p>
                    )}

                    {/* Meta */}
                    <div className="place-meta">
                        {place.bestTimeToVisit && (
                            <div>
                                <strong>Best time</strong>
                                <span>{place.bestTimeToVisit}</span>
                            </div>
                        )}
                        {place.recommendedTime && (
                            <div>
                                <strong>Recommended stay</strong>
                                <span>{place.recommendedTime}</span>
                            </div>
                        )}
                        {place.entryFee && (
                            <div>
                                <strong>Entry fee</strong>
                                <span>{place.entryFee}</span>
                            </div>
                        )}
                    </div>

                    {/* Tips */}
                    {Array.isArray(place.tips) && place.tips.length > 0 && (
                        <section className="place-tips">
                            <h4>Travel tips</h4>
                            <ul>
                                {place.tips.map((tip, i) => (
                                    <li key={i}>{tip}</li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* Actions */}
                    <div className="modal-actions">
                        <button
                            className="btn-save"
                            onClick={() => navigate(`/${place.slug}`)}
                        >
                            Open full page →
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
